import { useRef } from 'react'
import { FirebaseError } from 'firebase/app'
import { appState } from '@/store/appState'
import { writeReassurance } from '@/data/reassurances'
import { callSendReassurance } from '@/firebase/functions'
import { useToast } from '@/components/ui/ToastProvider'
import { tr } from '@/i18n'
import type { AlertRecord } from '@/types/firestore'

/* The send-reassurance chain, shared verbatim by the initial and directions
 * views (only the post-success delay differs: 2000 vs 1000 ms).
 *
 * Faithful sequence (actions_bottom_sheet_widget.dart:180-259 / 534-615):
 *  1. guard _model.isLoading (ONE flag shared by both views — same model);
 *  2. isLoading = true; sendReassuranceAnimation = true; update();
 *  3. fire-and-forget Firestore write (unawaited — its errors are swallowed
 *     and the doc lands even when the callable later fails);
 *  4. await sendReassurance(userId, alertRef=FULL PATH);
 *  5. success: wait delayMs -> overlay off + update() -> success toast;
 *     callable error: overlay off + update() -> error toast (#FF5963);
 *  6. isLoading = false.
 * A NON-callable error escapes past step 6 -> isLoading stays true -> the
 * button is dead until remount. Parity-kept (the throw propagates). */

export function useSendReassurance(
  userId: string | null,
  alerts: AlertRecord[] | null,
) {
  const isLoading = useRef(false) // _model.isLoading — shared across views
  const toast = useToast()

  return async function send(delayMs: 2000 | 1000): Promise<void> {
    if (isLoading.current) return
    isLoading.current = true

    appState.sendReassuranceAnimation = true
    appState.update()

    // unawaited(...) — deliberately not awaited, errors intentionally lost.
    void writeReassurance(userId!, alerts?.[0]?.ref.id).catch(() => {})

    let succeeded: boolean
    try {
      await callSendReassurance({
        userId: userId!,
        alertRef: alerts![0]!.ref.path, // force-unwraps, as in Dart
      })
      succeeded = true
    } catch (e) {
      if (e instanceof FirebaseError) {
        succeeded = false // FirebaseFunctionsException branch
      } else {
        throw e // parity: isLoading stays true, button dead
      }
    }

    if (succeeded) {
      await new Promise((r) => setTimeout(r, delayMs))
      appState.sendReassuranceAnimation = false
      appState.update()
      toast.show(tr.toasts.reassuranceSent)
    } else {
      appState.sendReassuranceAnimation = false
      appState.update()
      toast.show(tr.toasts.reassuranceError, 'var(--error)')
    }
    isLoading.current = false
  }
}
